import React, { useState, useRef, useEffect } from "react";
import { Button, Progress, Modal, notification } from "antd";
import { PlayCircle, StopCircle, RotateCcw, Check } from "lucide-react";
import { useGeneratePresignedUrlMutation } from "../../store/api/apiSlice";
import {
  uploadToPresignedUrl,
  generateFileName,
} from "../../utils/presignedUploader";

const VideoRecorder = ({ onVideoRecorded, maxDuration = 180, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();
  const [isUploading, setIsUploading] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Clean up function for stopping all media tracks
  const cleanupMedia = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupMedia();
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      // Video constraints to reduce file size: max 480p resolution for smaller files
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 854, max: 854 },
          height: { ideal: 480, max: 480 },
          frameRate: { ideal: 24, max: 24 },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 32000,
        },
      });

      streamRef.current = stream;
      videoRef.current.srcObject = stream;

      // MediaRecorder options with lower bitrate constraints for smaller file size
      const options = {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 1000000, // 1 Mbps - significantly reduces file size
        audioBitsPerSecond: 64000, // 64 kbps - lower audio bitrate
      };

      // Fallback to VP8 if VP9 not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = "video/webm;codecs=vp8";
      }

      // Fallback to MP4 for Safari
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = "video/mp4";
        // Remove bitrate constraints for MP4 as it may not be supported
        delete options.videoBitsPerSecond;
        delete options.audioBitsPerSecond;
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, {
          type: options.mimeType || "video/webm",
        });
        setRecordedVideo(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxDuration) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      notification.error({
        message: "Camera Error",
        description:
          "Failed to access camera or microphone. Please check your permissions.",
      });
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      // Don't stop the tracks yet as we might want to preview the video
    }
  };

  const resetRecording = () => {
    cleanupMedia();
    setRecordedVideo(null);
  };

  const handleUseRecording = async () => {
    try {
      setIsUploading(true);

      // Generate filename and get content type
      const contentType = recordedVideo.type || "video/mp4";
      const fileName = generateFileName(contentType, "recorded-video");

      // Get presigned URL from backend
      const presignedResponse = await generatePresignedUrl({
        fileName,
        contentType,
        expiresIn: 600, // 10 minutes
      }).unwrap();

      if (!presignedResponse?.url) {
        throw new Error("Failed to get presigned URL");
      }

      // Upload directly to S3
      await uploadToPresignedUrl(
        presignedResponse.url,
        recordedVideo,
        contentType
      );

      // Use the fileUrl from the response
      const fileUrl = presignedResponse.fileUrl;

      if (fileUrl) {
        onVideoRecorded(fileUrl);
        cleanupMedia(); // Clean up after successful upload
      } else {
        throw new Error("No file URL returned from server");
      }
    } catch (error) {
      console.error("Upload error:", error);
      notification.error({
        message: "Upload Failed",
        description: error.message || "Failed to upload video",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full mx-auto p-0 space-y-4">
      <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-video">
        <video ref={videoRef} autoPlay className="w-full h-full object-cover" />

        {isRecording && (
          <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            {formatTime(recordingTime)}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {isRecording && (
          <div className="space-y-2">
            <Progress
              percent={(recordingTime / maxDuration) * 100}
              status="active"
              showInfo={false}
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>{formatTime(recordingTime)}</span>
              <span>{formatTime(maxDuration)}</span>
            </div>
          </div>
        )}

        <div className="flex justify-center gap-4">
          {!recordedVideo ? (
            <Button
              type={isRecording ? "primary" : "default"}
              danger={isRecording}
              onClick={isRecording ? stopRecording : startRecording}
              icon={isRecording ? <StopCircle /> : <PlayCircle />}
            >
              {isRecording ? "Stop Recording" : "Start Recording"}
            </Button>
          ) : (
            <>
              <Button onClick={resetRecording} icon={<RotateCcw />}>
                Record Again
              </Button>
              <Button
                type="primary"
                onClick={handleUseRecording}
                icon={<Check />}
                loading={isUploading}
              >
                {isUploading ? "Uploading..." : "Use Recording"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoRecorder;
